import { supabase } from '../lib/supabase';

interface UploadBrandLogoResult {
  url: string;
  error?: string;
}

export const uploadBrandLogo = async (file: File): Promise<UploadBrandLogoResult> => {
  try {
    // Validate file
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload PNG, JPG, or SVG.');
    }

    // 5MB limit
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('File size must be less than 5MB.');
    }

    // Get user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/brand-logo-${Date.now()}.${fileExt}`;

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('brand-assets')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (error) throw error;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('brand-assets')
      .getPublicUrl(data.path);

    return { url: publicUrl };
  } catch (error: any) {
    console.error('Error uploading brand logo:', error);
    return { url: '', error: error.message || 'Failed to upload logo' };
  }
};

export const deleteBrandLogo = async (logoUrl: string): Promise<void> => {
  try {
    // Extract file path from URL
    const urlParts = logoUrl.split('/');
    const filePath = urlParts.slice(-2).join('/'); // user_id/filename

    const { error } = await supabase.storage
      .from('brand-assets')
      .remove([filePath]);

    if (error) throw error;
  } catch (error: any) {
    console.error('Error deleting brand logo:', error);
    throw error;
  }
};
